import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CInputGroup,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  changeUserPassword,
  ChangePasswordRequestStatus,
} from "../../api/change-password";
import { PasswordChangeData, newPasswordChangeData } from "../../models/user";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";

interface UserFormProps {
  initialData?: PasswordChangeData;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const UserChangePasswordForm: React.FC<UserFormProps> = ({
  initialData,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [editingData, setEditingData] = useState<PasswordChangeData>(
    initialData ? initialData : newPasswordChangeData()
  );
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingData({
      ...editingData,
      currentPassword: e.target.value,
    });
  };

  const onPassword1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingData({
      ...editingData,
      newPassword1: e.target.value,
    });
  };

  const onPassword2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingData({
      ...editingData,
      newPassword2: e.target.value,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let requestPromise: Promise<ChangePasswordRequestStatus>;

    requestPromise = changeUserPassword(editingData);

    const changePasswordStatus = await requestPromise;

    if (changePasswordStatus.status !== SUCCESS) {
      if (changePasswordStatus.errors !== undefined) {
        setErrors(changePasswordStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (changePasswordStatus.detail !== undefined) {
        message = changePasswordStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const onOldPasswordShowClick = () => {
    setShowOldPassword(!showOldPassword);
  };

  const onPassword1ShowClick = () => {
    setShowPassword1(!showPassword1);
  };

  const onPassword2ShowClick = () => {
    setShowPassword2(!showPassword2);
  };

  const clearForm = () => {
    setEditingData(initialData ? initialData : newPasswordChangeData());
  };

  useEffect(() => {
    setEditingData(initialData ? initialData : newPasswordChangeData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Contraseña actual</CLabel>
            </CCol>
            <CCol>
              <CInputGroup>
                <CInput
                  type={showOldPassword ? "text" : "password"}
                  value={editingData.currentPassword}
                  onChange={onOldPasswordChange}
                  placeholder="Ej: Contraseña"
                ></CInput>
                <CButton
                  type="button"
                  color="primary"
                  variant="outline"
                  onClick={onOldPasswordShowClick}
                >
                  {showOldPassword ? (
                    <i className="fa fa-eye-slash"></i>
                  ) : (
                    <i className="fa fa-eye"></i>
                  )}
                </CButton>
              </CInputGroup>
              <FieldErrors
                errors={getFieldErrors("currentPassword", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nueva contraseña</CLabel>
            </CCol>
            <CCol>
              <CInputGroup>
                <CInput
                  type={showPassword1 ? "text" : "password"}
                  value={editingData.newPassword1}
                  onChange={onPassword1Change}
                  placeholder="Ej: Contraseña"
                ></CInput>
                <CButton
                  type="button"
                  color="primary"
                  variant="outline"
                  onClick={onPassword1ShowClick}
                >
                  {showPassword1 ? (
                    <i className="fa fa-eye-slash"></i>
                  ) : (
                    <i className="fa fa-eye"></i>
                  )}
                </CButton>
              </CInputGroup>
              <FieldErrors
                errors={getFieldErrors("newPassword1", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Repita la nueva contraseña</CLabel>
            </CCol>
            <CCol>
              <CInputGroup>
                <CInput
                  type={showPassword2 ? "text" : "password"}
                  value={editingData.newPassword2}
                  onChange={onPassword2Change}
                  placeholder="Ej: Repita la Contraseña"
                ></CInput>
                <CButton
                  type="button"
                  color="primary"
                  variant="outline"
                  onClick={onPassword2ShowClick}
                >
                  {showPassword2 ? (
                    <i className="fa fa-eye-slash"></i>
                  ) : (
                    <i className="fa fa-eye"></i>
                  )}
                </CButton>
              </CInputGroup>
              <FieldErrors
                errors={getFieldErrors("newPassword2", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default UserChangePasswordForm;
